import { Modal } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';
import type { TemplateDayDto, TemplateDecisionRowDto, TemplateListItemDto, TemplateSectionDto } from '@pams/shared';
import { templateDecisionHref } from '@/lib/templateLinks';
import { BookletChip } from '../components/BookletChip';
import { OPEN_DECISIONS_COPY } from '../copy';
import { createPostTypeHref } from '../Decisions/decisionRules';
import { DecisionModal } from './DecisionModal';
import type { DecisionModalTarget } from './decisionModal';
import {
  chipOf,
  inChargeOnlyOf,
  noDecisionFreesATemplate,
  openDecisionsOf,
  rowWordsOf,
  sourceStatusOf,
  type OpenDecisions,
  type OpenDecisionRow,
} from './openDecisions';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §3a] WHAT HOLDS TEMPLATES BACK — above the library, every open decision with the number of
 *  templates waiting on it, the one that frees the most first, and a way to decide it.
 *
 *  ⚠️ IT DECIDES NOTHING. "Decide" opens the decisions page on that code's own form, and "Create a post
 *  type" opens the post type master's own drawer: each is its own deliberate act, with its own audit
 *  row, on the page that already validates it ([P7B §3c]'s precedent for a policy).
 *
 *  ⚠️ FIVE ROWS, THEN "SHOW ALL" — a `<button aria-expanded>`, never an overlay. The unusual columns'
 *  decisions sit in their own block ([P7B.1 §3c]), and the codes that hold nothing back in another, so
 *  this panel's numbers and the set's add up.
 *
 *  [amended P7C §4] A PERSON CAN NOW DECIDE IN PLACE — "Preview", before "Decide" on every row, opens the
 *  decisions page's own form in a dialog (`DecisionModal`), saved by the page's own hook. The panel
 *  itself still decides nothing, "Decide" still opens the page, and a waiting marker is still a
 *  sentence with no button. The overlay census moves by ONE, and that one is swept.
 *
 *  ⚠️ [amended P7C.1 §2] THE PANEL IS NOW A LINK ABOVE THE LIBRARY, STATING THE OUTSTANDING COUNT, AND IT OPENS
 *  THE SAME CONTENT IN A DIALOG — the panel crowded the library. `OpenDecisionsBody` is the old panel's JSX moved
 *  verbatim, so nothing it held is lost (`Library.test.tsx` holds the exact multiset). "Show all" is still a
 *  disclosure, never an overlay, now inside the dialog, and starts closed on each open (`destroyOnHidden`).
 *    · The link is shown at zero too: the dialog still holds the "everything decided" lead and the blocks below.
 *    · `DecisionModal` is a SIBLING of the panel's dialog, its state outside the destroyable body: opened from
 *      inside the panel, its portal is appended after the panel's at the same z-index, so it lands on top, and
 *      rc-dialog handles Escape on its own wrap — Escape closes the preview and leaves the panel open.
 *    · ⚠️ THE DIALOG CLOSES WHEN THE PATHNAME MOVES, adjusted DURING RENDER from `closeOn` — never derived from
 *      `location.key`, which every history entry keeps, so Back to the entry it was opened on would reopen it.
 *      The one link inside it that stays in the library (an unusual template) therefore closes it; every other
 *      link leaves the library, which unmounts it. The overlay census moves by one more, swept.
 *    · The decisions page's `SOURCE` read stays EAGER in the layout: the library's four-request census is pinned,
 *      and a read deferred to the click would flash the chips on open.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const ROWS_SHOWN = 5;

const PreviewButton = ({ words, onPreview }: { words: string; onPreview: () => void }): React.ReactElement => (
  <button
    type="button"
    className="text-link"
    data-testid="open-decision-preview"
    aria-label={OPEN_DECISIONS_COPY.previewLabel(words)}
    onClick={onPreview}
  >
    {OPEN_DECISIONS_COPY.preview}
  </button>
);

const DecisionLine = ({
  row,
  setKey,
  chip,
  mayCreatePostType,
  onPreview,
}: {
  row: OpenDecisionRow;
  setKey: string;
  chip: ReturnType<typeof chipOf>;
  mayCreatePostType: boolean;
  onPreview: (target: DecisionModalTarget) => void;
}): React.ReactElement => (
  <li
    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1"
    data-testid="open-decision"
    data-class={row.class}
    data-code={row.code}
  >
    <BookletChip variant={chip.variant}>{chip.text}</BookletChip>
    <span className="font-medium text-foreground">{rowWordsOf(row)}</span>
    <span className="text-muted-foreground">
      {OPEN_DECISIONS_COPY.heldBack(row.heldBack.length)}
      {row.onlyFor > 0 ? ` · ${OPEN_DECISIONS_COPY.onlyFor(row.onlyFor)}` : ''}
    </span>
    <PreviewButton
      words={rowWordsOf(row)}
      onPreview={() => {
        onPreview({ class: row.class, code: row.code, words: rowWordsOf(row) });
      }}
    />
    <Link
      to={templateDecisionHref(setKey, row.class, row.code)}
      className="text-link"
      data-testid="open-decision-decide"
    >
      {OPEN_DECISIONS_COPY.decide}
    </Link>
    {row.class === 'FORCE_COLUMN' && mayCreatePostType ? (
      <Link to={createPostTypeHref(setKey, row.code)} className="text-link" data-testid="open-decision-post-type">
        {OPEN_DECISIONS_COPY.createPostType}
      </Link>
    ) : null}
  </li>
);

type PanelData = {
  setKey: string;
  items: readonly TemplateListItemDto[];
  sections: readonly TemplateSectionDto[];
  /** The set's own count of open decisions — the decisions page states the same number. */
  outstanding: number;
  /** Templates using an unusual column, by id, with those columns. */
  unusual: ReadonlyMap<string, readonly string[]>;
  /** The decisions page's `SOURCE` list — the review's reading of each code, and the in-charge-only codes. */
  sources: readonly TemplateDecisionRowDto[] | undefined;
  mayCreatePostType: boolean;
};

/** [P7C.1 §2] The panel's content, moved VERBATIM from the `<details>` card into the dialog. */
const OpenDecisionsBody = ({
  setKey,
  items,
  sections,
  outstanding,
  unusual,
  sources,
  mayCreatePostType,
  open,
  onPreview: preview,
}: PanelData & {
  open: OpenDecisions;
  onPreview: (target: DecisionModalTarget) => void;
}): React.ReactElement => {
  /* Reset with the dialog: `destroyOnHidden` unmounts this body when it closes. */
  const [all, setAll] = useState(false);
  const status = sourceStatusOf(sources);
  const inChargeOnly = inChargeOnlyOf(sources);
  const ready = items.filter((item) => item.readiness.state === 'READY').length;
  const notBuilt = items.filter(
    (item) => item.readiness.state === 'ROUND_THE_CLOCK' || item.readiness.state === 'EMPTY',
  ).length;
  const shown = all ? open.rows : open.rows.slice(0, ROWS_SHOWN);
  const unusualItems = [...items].filter((item) => unusual.has(item.id)).sort((a, b) => a.ordinal - b.ordinal);
  const line = (row: OpenDecisionRow) => (
    <DecisionLine
      key={row.key}
      row={row}
      setKey={setKey}
      chip={chipOf(row, status, sections)}
      mayCreatePostType={mayCreatePostType}
      onPreview={preview}
    />
  );

  return (
    <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1 text-sm" data-testid="open-decisions">
      <p data-testid="open-decisions-lead">
        {OPEN_DECISIONS_COPY.lead(ready, items.length, notBuilt)}{' '}
        {outstanding === 0
          ? OPEN_DECISIONS_COPY.everythingDecided
          : OPEN_DECISIONS_COPY.open(outstanding, open.holdingBack)}
      </p>
      {noDecisionFreesATemplate(open) ? (
        <p className="text-muted-foreground" data-testid="open-decisions-none-alone">
          {OPEN_DECISIONS_COPY.noneAlone}
        </p>
      ) : null}

      {open.rows.length > 0 ? (
        <ol className="flex flex-col divide-y divide-border" data-testid="open-decisions-rows">
          {shown.map(line)}
        </ol>
      ) : null}
      {open.rows.length > ROWS_SHOWN ? (
        <button
          type="button"
          className="self-start text-link"
          aria-expanded={all}
          data-testid="open-decisions-all"
          onClick={() => {
            setAll((current) => !current);
          }}
        >
          {all ? OPEN_DECISIONS_COPY.showFewer : OPEN_DECISIONS_COPY.showAll(open.rows.length)}
        </button>
      ) : null}

      {open.waiting.map((marker) => (
        <p key={marker.class} className="text-muted-foreground" data-testid={`open-decisions-waiting-${marker.class}`}>
          {OPEN_DECISIONS_COPY.waiting[marker.class](marker.templates)}
        </p>
      ))}

      {unusualItems.length > 0 ? (
        <section
          className="flex flex-col gap-1.5 border-t border-border pt-3"
          data-testid="unusual-block"
          aria-label={OPEN_DECISIONS_COPY.unusualTitle(unusualItems.length)}
        >
          <h3 className="font-medium text-heading">{OPEN_DECISIONS_COPY.unusualTitle(unusualItems.length)}</h3>
          <ul className="flex flex-col gap-0.5">
            {unusualItems.map((item) => (
              <li key={item.id} className="flex flex-wrap items-baseline gap-x-2" data-testid="unusual-template">
                <Link to={`/templates/${setKey}/t/${item.id}`} className="text-link">
                  {item.title}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {OPEN_DECISIONS_COPY.unusualColumns(unusual.get(item.id) ?? [])}
                </span>
              </li>
            ))}
          </ul>
          {open.unusual.length > 0 ? (
            <ol className="flex flex-col divide-y divide-border" data-testid="unusual-decisions">
              {open.unusual.map(line)}
            </ol>
          ) : null}
        </section>
      ) : null}

      {inChargeOnly.length > 0 ? (
        <section className="flex flex-col gap-1.5 border-t border-border pt-3" data-testid="in-charge-only">
          <h3 className="font-medium text-heading">{OPEN_DECISIONS_COPY.inChargeTitle(inChargeOnly.length)}</h3>
          <ul className="flex flex-col gap-0.5">
            {inChargeOnly.map((row) => {
              /* A unit named as guidance — a SOURCE code, so the review's reading of a source. */
              const chip = chipOf({ class: 'SOURCE', code: row.code }, status, sections);
              return (
                <li key={row.code} className="flex flex-wrap items-baseline gap-x-2" data-testid="in-charge-only-code">
                  <BookletChip variant={chip.variant}>{chip.text}</BookletChip>
                  <span className="font-medium text-foreground">{row.label}</span>
                  <span className="text-muted-foreground">{OPEN_DECISIONS_COPY.inChargeRow(row.usedBy)}</span>
                  <PreviewButton
                    words={OPEN_DECISIONS_COPY.words.source(row.label)}
                    onPreview={() => {
                      preview({
                        class: 'SOURCE',
                        code: row.code,
                        words: OPEN_DECISIONS_COPY.words.source(row.label),
                      });
                    }}
                  />
                  <Link to={templateDecisionHref(setKey, 'SOURCE', row.code)} className="text-link">
                    {OPEN_DECISIONS_COPY.decide}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <p className="text-xs text-muted-foreground">{OPEN_DECISIONS_COPY.legend}</p>
    </div>
  );
};

export const OpenDecisionsPanel = ({
  closeOn,
  rareColumns,
  days,
  ...data
}: PanelData & {
  rareColumns: ReadonlyMap<string, number>;
  /** The set's festival days — the schedule form in the preview names them. */
  days: readonly TemplateDayDto[];
  /** [P7C.1 §2] The library's pathname: when it moves, both dialogs close. */
  closeOn: string;
}): React.ReactElement => {
  const [open, setOpen] = useState(false);
  /* The decision the preview shows — kept after closing, so the closing dialog never goes blank. */
  const [target, setTarget] = useState<DecisionModalTarget | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /* ⚠️ Adjusted DURING RENDER (React's documented pattern), never derived from `location.key` — see the header. */
  const [closedFor, setClosedFor] = useState(closeOn);
  if (closedFor !== closeOn) {
    setClosedFor(closeOn);
    setOpen(false);
    setPreviewing(false);
  }
  const preview = (next: DecisionModalTarget): void => {
    setTarget(next);
    setPreviewing(true);
  };
  const decisions = openDecisionsOf(data.items, rareColumns);

  return (
    <>
      <button
        type="button"
        className="self-start text-sm text-link"
        data-testid="open-decisions-link"
        onClick={() => {
          setOpen(true);
        }}
      >
        {OPEN_DECISIONS_COPY.link(data.outstanding)}
      </button>
      <Modal
        open={open}
        title={OPEN_DECISIONS_COPY.title}
        footer={null}
        width={720}
        centered
        destroyOnHidden
        onCancel={() => {
          setOpen(false);
        }}
      >
        <OpenDecisionsBody {...data} open={decisions} onPreview={preview} />
      </Modal>
      <DecisionModal
        open={previewing}
        onClose={() => {
          setPreviewing(false);
        }}
        setKey={data.setKey}
        target={target}
        days={days}
        mayCreatePostType={data.mayCreatePostType}
      />
    </>
  );
};
